import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, X, Sparkles, BookOpen, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useGameSounds } from '@/hooks/useGameSounds';

interface TarotCardQuizProps {
  cards: any[]; // Now accepts multiple cards for quiz
  onComplete: (score: number) => void;
  onClose: () => void;
}

export default function TarotCardQuiz({ cards, onComplete, onClose }: TarotCardQuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [startTime] = useState(Date.now());
  const [questions, setQuestions] = useState<any[]>([]);
  const { playSound, preloadSounds, soundsEnabled, toggleSounds } = useGameSounds();

  useEffect(() => {
    preloadSounds(['correct', 'incorrect', 'levelComplete']);
  }, [preloadSounds]);

  useEffect(() => {
    generateQuestions();
  }, [cards]);

  const generateQuestions = () => {
    const allCards = cards.length > 0 ? cards : [];
    const generatedQuestions: any[] = [];

    if (allCards.length === 0) return;

    const cardIndices = allCards.map((_, i) => i);
    const shuffledIndices = [...cardIndices].sort(() => Math.random() - 0.5);
    const numQuestions = Math.min(15, allCards.length * 5);
    
    // 15 different question types for maximum variety
    const questionTypes = [
      'scenario-career', 'scenario-love', 'scenario-spiritual', 'keyword-match',
      'reversed', 'upright', 'comparison', 'position-past', 'position-present',
      'position-future', 'position-advice', 'helping-others', 'symbol', 'element', 'timing'
    ];
    
    for (let i = 0; i < numQuestions; i++) {
      const cardIndex = shuffledIndices[i % shuffledIndices.length];
      const card = allCards[cardIndex];
      const questionType = questionTypes[i % questionTypes.length];
      const cardName = card.card_name || card.name;

      switch(questionType) {
        case 'scenario-career':
          const careerScenarios = [
            `If ${cardName} appears in a career reading, what guidance does it offer?`,
            `When consulting about a job change, ${cardName} suggests:`,
            `In a work conflict situation, ${cardName} advises:`,
            `For someone seeking career direction, ${cardName} indicates:`
          ];
          generatedQuestions.push({
            type: 'scenario',
            question: careerScenarios[Math.floor(Math.random() * careerScenarios.length)],
            options: [
              `${card.keywords?.[0] || 'Change'} and ${card.keywords?.[1] || 'growth'} in your professional path`,
              ...getRandomScenarioOptions(card, allCards, 3)
            ].sort(() => Math.random() - 0.5),
            correct: `${card.keywords?.[0] || 'Change'} and ${card.keywords?.[1] || 'growth'} in your professional path`,
            cardName
          });
          break;

        case 'scenario-love':
          const loveScenarios = [
            `In a relationship reading, ${cardName} reveals:`,
            `When asking about romantic potential, ${cardName} suggests:`,
            `For healing after heartbreak, ${cardName} advises:`,
            `In a love triangle situation, ${cardName} indicates:`
          ];
          generatedQuestions.push({
            type: 'scenario',
            question: loveScenarios[Math.floor(Math.random() * loveScenarios.length)],
            options: [
              `Emotional ${card.keywords?.[0] || 'healing'} and ${card.keywords?.[1] || 'new connections'}`,
              'Passionate intensity and physical attraction',
              'Communication challenges requiring patience',
              'Commitment and long-term stability'
            ].sort(() => Math.random() - 0.5),
            correct: `Emotional ${card.keywords?.[0] || 'healing'} and ${card.keywords?.[1] || 'new connections'}`,
            cardName
          });
          break;

        case 'scenario-spiritual':
          const spiritualScenarios = [
            `For spiritual growth, ${cardName} suggests:`,
            `In meditation, ${cardName} guides you toward:`,
            `When seeking life purpose, ${cardName} reveals:`,
            `During a spiritual awakening, ${cardName} indicates:`
          ];
          generatedQuestions.push({
            type: 'scenario',
            question: spiritualScenarios[Math.floor(Math.random() * spiritualScenarios.length)],
            options: [
              `${card.keywords?.[2] || 'Inner wisdom'} and spiritual ${card.keywords?.[0] || 'transformation'}`,
              'External validation and recognition',
              'Material achievements and success',
              'Social connections and networking'
            ].sort(() => Math.random() - 0.5),
            correct: `${card.keywords?.[2] || 'Inner wisdom'} and spiritual ${card.keywords?.[0] || 'transformation'}`,
            cardName
          });
          break;

        case 'keyword-match':
          const targetCard = allCards[Math.floor(Math.random() * allCards.length)];
          const keywordIdx = Math.floor(Math.random() * (targetCard.keywords?.length || 1));
          generatedQuestions.push({
            type: 'keyword-match',
            question: `Which card embodies the energy of "${targetCard.keywords?.[keywordIdx] || 'transformation'}"?`,
            options: [
              targetCard.card_name || targetCard.name,
              ...allCards.filter(c => c.id !== targetCard.id).slice(0, 3).map(c => c.card_name || c.name)
            ].sort(() => Math.random() - 0.5),
            correct: targetCard.card_name || targetCard.name,
            cardName: 'Card Recognition'
          });
          break;

        case 'reversed':
          const reversedQuestions = [
            `When ${cardName} appears reversed, what challenge might the querent face?`,
            `${cardName} reversed warns against:`,
            `The reversed ${cardName} indicates a need to address:`,
            `In a reading, ${cardName} reversed suggests:`
          ];
          generatedQuestions.push({
            type: 'reversed',
            question: reversedQuestions[Math.floor(Math.random() * reversedQuestions.length)],
            options: [
              card.reversed_meaning || card.reversedMeaning,
              ...getRandomMeaningsFromOtherCards(card, allCards, 3, 'reversed')
            ].sort(() => Math.random() - 0.5),
            correct: card.reversed_meaning || card.reversedMeaning,
            cardName
          });
          break;

        case 'upright':
          const uprightQuestions = [
            `The upright ${cardName} primarily represents:`,
            `When ${cardName} appears upright, it signifies:`,
            `In its upright position, ${cardName} teaches us about:`,
            `The core message of ${cardName} is:`
          ];
          generatedQuestions.push({
            type: 'upright',
            question: uprightQuestions[Math.floor(Math.random() * uprightQuestions.length)],
            options: [
              card.upright_meaning || card.uprightMeaning,
              ...getRandomMeaningsFromOtherCards(card, allCards, 3, 'upright')
            ].sort(() => Math.random() - 0.5),
            correct: card.upright_meaning || card.uprightMeaning,
            cardName
          });
          break;

        case 'position-past':
          generatedQuestions.push({
            type: 'position',
            question: `In the PAST position of a three-card spread, ${cardName} indicates:`,
            options: [
              `Past experiences with ${card.keywords?.[0] || 'change'} that shaped your current situation`,
              'Future potential waiting to manifest',
              'Current challenges requiring immediate attention',
              'Hidden influences affecting outcomes'
            ].sort(() => Math.random() - 0.5),
            correct: `Past experiences with ${card.keywords?.[0] || 'change'} that shaped your current situation`,
            cardName
          });
          break;

        case 'position-present':
          generatedQuestions.push({
            type: 'position',
            question: `In the PRESENT position of a Celtic Cross spread, ${cardName} shows:`,
            options: [
              `Current energy of ${card.keywords?.[0] || 'transformation'} actively influencing your life`,
              'Distant past events no longer relevant',
              'Potential outcomes years away',
              'Other people\'s opinions about you'
            ].sort(() => Math.random() - 0.5),
            correct: `Current energy of ${card.keywords?.[0] || 'transformation'} actively influencing your life`,
            cardName
          });
          break;

        case 'position-future':
          generatedQuestions.push({
            type: 'position',
            question: `In the OUTCOME position of a spread, ${cardName} predicts:`,
            options: [
              `Future manifestation of ${card.keywords?.[1] || 'growth'} if current path continues`,
              'Events that have already happened',
              'Someone else\'s future, not yours',
              'Irrelevant information'
            ].sort(() => Math.random() - 0.5),
            correct: `Future manifestation of ${card.keywords?.[1] || 'growth'} if current path continues`,
            cardName
          });
          break;

        case 'position-advice':
          generatedQuestions.push({
            type: 'position',
            question: `As ADVICE in a spread, ${cardName} counsels you to:`,
            options: [
              `Embrace ${card.keywords?.[0] || 'change'} and embody ${card.keywords?.[1] || 'wisdom'}`,
              'Ignore your intuition and follow logic only',
              'Wait passively for others to act',
              'Avoid all risks and stay comfortable'
            ].sort(() => Math.random() - 0.5),
            correct: `Embrace ${card.keywords?.[0] || 'change'} and embody ${card.keywords?.[1] || 'wisdom'}`,
            cardName
          });
          break;

        case 'helping-others':
          const helpingQuestions = [
            `When reading for a client and ${cardName} appears, you might say:`,
            `To help someone understand ${cardName}, explain that:`,
            `If a client fears ${cardName}, reassure them by saying:`,
            `When ${cardName} appears for a beginner, emphasise:`
          ];
          generatedQuestions.push({
            type: 'helping',
            question: helpingQuestions[Math.floor(Math.random() * helpingQuestions.length)],
            options: [
              `This card invites ${card.keywords?.[0] || 'transformation'} and offers ${card.keywords?.[1] || 'wisdom'} for your journey`,
              'This is a bad omen that cannot be changed',
              'This card has no meaning in modern readings',
              'Ignore this card and draw another'
            ].sort(() => Math.random() - 0.5),
            correct: `This card invites ${card.keywords?.[0] || 'transformation'} and offers ${card.keywords?.[1] || 'wisdom'} for your journey`,
            cardName
          });
          break;

        case 'comparison':
          if (allCards.length > 1) {
            const otherCard = allCards.find(c => c.id !== card.id) || allCards[0];
            const contexts = [
              { situation: 'new beginnings', keywords: ['begin', 'new', 'start', 'fresh'] },
              { situation: 'emotional healing', keywords: ['heal', 'emotion', 'feel', 'heart'] },
              { situation: 'mental clarity', keywords: ['clear', 'logic', 'think', 'mind'] },
              { situation: 'taking action', keywords: ['action', 'move', 'do', 'active'] },
              { situation: 'patience and waiting', keywords: ['wait', 'patient', 'pause', 'rest'] }
            ];
            const context = contexts[Math.floor(Math.random() * contexts.length)];
            
            generatedQuestions.push({
              type: 'comparison',
              question: `For guidance about ${context.situation}, which card is more relevant?`,
              options: [
                cardName,
                otherCard.card_name || otherCard.name,
                ...allCards.slice(2, 4).map(c => c.card_name || c.name)
              ].filter(Boolean).slice(0, 4).sort(() => Math.random() - 0.5),
              correct: (card.keywords || []).some(k => 
                context.keywords.some(ck => k.toLowerCase().includes(ck))
              ) ? cardName : (otherCard.card_name || otherCard.name),
              cardName: 'Card Comparison'
            });
          }
          break;

        case 'symbol':
          const symbols = card.keywords || ['transformation', 'wisdom', 'growth'];
          generatedQuestions.push({
            type: 'symbol',
            question: `What symbolic meaning does ${cardName} carry in tarot readings?`,
            options: [
              `${symbols[0]}, ${symbols[1]}, and ${symbols[2] || 'enlightenment'}`,
              'Fear, anger, and destruction',
              'Greed, manipulation, and deceit',
              'Confusion, chaos, and disorder'
            ].sort(() => Math.random() - 0.5),
            correct: `${symbols[0]}, ${symbols[1]}, and ${symbols[2] || 'enlightenment'}`,
            cardName
          });
          break;

        case 'element':
          const elementMap: any = {
            'wands': 'Fire - passion, creativity, action',
            'cups': 'Water - emotions, intuition, relationships',
            'swords': 'Air - intellect, communication, truth',
            'pentacles': 'Earth - material, practical, physical'
          };
          const cardSuit = (card.suit || '').toLowerCase();
          const correctElement = elementMap[cardSuit] || 'Spirit - divine guidance and wisdom';
          
          generatedQuestions.push({
            type: 'element',
            question: `What elemental energy does ${cardName} carry?`,
            options: [
              correctElement,
              ...Object.values(elementMap).filter(e => e !== correctElement).slice(0, 3)
            ].sort(() => Math.random() - 0.5),
            correct: correctElement,
            cardName
          });
          break;

        case 'timing':
          generatedQuestions.push({
            type: 'timing',
            question: `When might the energy of ${cardName} manifest in a querent's life?`,
            options: [
              `When they experience ${card.keywords?.[0] || 'change'} and need ${card.keywords?.[1] || 'wisdom'}`,
              'Never, this card has no timing associations',
              'Only during full moons in winter',
              'Randomly without any pattern'
            ].sort(() => Math.random() - 0.5),
            correct: `When they experience ${card.keywords?.[0] || 'change'} and need ${card.keywords?.[1] || 'wisdom'}`,
            cardName
          });
          break;
      }
    }

    const uniqueQuestions = generatedQuestions.filter((q, index, self) => 
      index === self.findIndex(t => t.question === q.question)
    );
    setQuestions(uniqueQuestions.sort(() => Math.random() - 0.5).slice(0, 12));
  };

  const getRandomScenarioOptions = (currentCard: any, allCards: any[], count: number) => {
    const scenarios = [
      'Financial stability and material success',
      'Emotional healing and self-reflection',
      'Intellectual breakthroughs and clear communication',
      'Spiritual growth and higher understanding',
      'Conflict and difficult decisions ahead',
      'Balance and patience required',
      'Taking bold action and leadership'
    ];
    
    return scenarios
      .filter(s => !s.toLowerCase().includes((currentCard.keywords?.[0] || '').toLowerCase()))
      .sort(() => Math.random() - 0.5)
      .slice(0, count);
  };

  const getRandomKeywordsFromOtherCards = (currentCard: any, allCards: any[], count: number) => {
    const otherCards = allCards.filter(c => c.id !== currentCard.id);
    const keywords: string[] = [];
    
    otherCards.forEach(card => {
      if (card.keywords && keywords.length < count) {
        keywords.push(...card.keywords.slice(0, 1));
      }
    });
    
    // Add some generic wrong answers if needed
    const genericKeywords = ['Power', 'Wisdom', 'Change', 'Balance', 'Truth', 'Love', 'Strength'];
    while (keywords.length < count) {
      const randomKeyword = genericKeywords[Math.floor(Math.random() * genericKeywords.length)];
      if (!keywords.includes(randomKeyword) && !(currentCard.keywords || []).includes(randomKeyword)) {
        keywords.push(randomKeyword);
      }
    }
    
    return keywords.slice(0, count);
  };

  const getRandomMeaningsFromOtherCards = (currentCard: any, allCards: any[], count: number, meaningType: 'upright' | 'reversed' = 'upright') => {
    const otherCards = allCards.filter(c => c.id !== currentCard.id);
    const meanings: string[] = [];
    
    otherCards.forEach(card => {
      if (meanings.length < count) {
        const meaning = meaningType === 'upright' 
          ? (card.upright_meaning || card.uprightMeaning || 'Unknown meaning')
          : (card.reversed_meaning || card.reversedMeaning || 'Unknown meaning');
        meanings.push(meaning);
      }
    });
    
    // Add generic wrong answers if needed
    const genericMeanings = meaningType === 'upright' 
      ? [
          'Represents confusion and chaos',
          'Signifies material success and abundance',
          'Indicates spiritual awakening and enlightenment',
          'Suggests facing challenges with courage'
        ]
      : [
          'Blocked energy and resistance',
          'Excess and imbalance',
          'Self-doubt and insecurity',
          'Avoiding responsibility'
        ];
    
    while (meanings.length < count) {
      const randomMeaning = genericMeanings[Math.floor(Math.random() * genericMeanings.length)];
      if (!meanings.includes(randomMeaning)) {
        meanings.push(randomMeaning);
      }
    }
    
    return meanings.slice(0, count);
  };

  const handleSubmit = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowReview(true);
    }
  };

  const handleFinishQuiz = async () => {
    let finalScore = 0;
    answers.forEach((answer, idx) => {
      if (answer === questions[idx].correct) {
        finalScore += 10;
      }
    });
    
    setScore(finalScore);
    setShowResults(true);
    playSound('levelComplete');
    saveQuizResults(finalScore);
  };

  const goToQuestion = (index: number) => {
    setCurrentQuestion(index);
    setShowReview(false);
  };

  const saveQuizResults = async (finalScore: number) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const timeElapsed = Math.floor((Date.now() - startTime) / 1000);

    // Save quiz completion for each card
    for (const card of cards) {
      await supabase.from('tarot_quiz_completions').insert({
        user_id: user.id,
        card_id: card.id,
        correct_answers: Math.floor(finalScore / 10),
        total_questions: questions.length,
        time_taken_seconds: timeElapsed
      });

      // Update mastery
      const { data: mastery } = await supabase
        .from('tarot_card_mastery')
        .select('*')
        .eq('user_id', user.id)
        .eq('card_id', card.id)
        .maybeSingle();

      const scorePercent = (finalScore / (questions.length * 10)) * 100;

      if (mastery) {
        await supabase
          .from('tarot_card_mastery')
          .update({
            times_quizzed: mastery.times_quizzed + 1,
            mastery_level: Math.min(5, mastery.mastery_level + (scorePercent > 60 ? 1 : 0)),
            last_reviewed_at: new Date().toISOString()
          })
          .eq('id', mastery.id);
      } else {
        await supabase.from('tarot_card_mastery').insert({
          user_id: user.id,
          card_id: card.id,
          mastery_level: scorePercent > 60 ? 1 : 0,
          times_quizzed: 1
        });
      }
    }
  };

  const updateAnswer = (value: string) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = value;
    setAnswers(newAnswers);
  };

  if (questions.length === 0) {
    return null;
  }

  const currentQ = questions[currentQuestion];

  if (showReview) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Review Your Answers</DialogTitle>
            <DialogDescription className="text-center">
              Review and change any answers before submitting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {questions.map((q, idx) => (
              <div key={idx} className="p-4 border rounded-lg space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm flex-1">
                    {idx + 1}. {q.question}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goToQuestion(idx)}
                  >
                    Edit
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your answer: <span className="font-medium">{answers[idx] || 'Not answered'}</span>
                </p>
              </div>
            ))}
            <Button 
              onClick={handleFinishQuiz}
              className="w-full bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary"
              disabled={answers.filter(Boolean).length !== questions.length}
            >
              Submit Quiz
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (showResults) {
    const percentScore = Math.round((score / (questions.length * 10)) * 100);
    
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Quiz Complete!</DialogTitle>
            <DialogDescription className="text-center">
              Review your results and learn from your mistakes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent text-background text-3xl font-bold mb-4">
                {percentScore}%
              </div>
              <p className="text-muted-foreground">
                You scored {score} out of {questions.length * 10} points!
              </p>
            </div>
            
            <div className="space-y-2">
              {percentScore === 100 && (
                <Badge className="w-full justify-center py-2 bg-gradient-to-r from-primary to-primary-glow">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Perfect Score! Tarot Master!
                </Badge>
              )}
              {percentScore >= 70 && percentScore < 100 && (
                <Badge className="w-full justify-center py-2 bg-gradient-to-r from-accent to-soft-accent">
                  <Check className="h-4 w-4 mr-2" />
                  Excellent Work!
                </Badge>
              )}
              {percentScore >= 50 && percentScore < 70 && (
                <Badge className="w-full justify-center py-2 bg-secondary" variant="secondary">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Good Progress - Keep Learning!
                </Badge>
              )}
              {percentScore < 50 && (
                <Badge className="w-full justify-center py-2" variant="outline">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Keep Studying - You'll Get There!
                </Badge>
              )}
            </div>

            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold text-lg">Answer Review</h3>
              {questions.map((q, idx) => {
                const isCorrect = answers[idx] === q.correct;
                return (
                  <div key={idx} className={`p-4 rounded-lg border-2 ${isCorrect ? 'border-green-500/50 bg-green-500/5' : 'border-red-500/50 bg-red-500/5'}`}>
                    <div className="flex items-start gap-2 mb-2">
                      {isCorrect ? (
                        <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 space-y-2">
                        <p className="font-medium text-sm">{idx + 1}. {q.question}</p>
                        <div className="space-y-1 text-sm">
                          <p className={isCorrect ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                            Your answer: <span className="font-medium">{answers[idx]}</span>
                          </p>
                          {!isCorrect && (
                            <p className="text-green-700 dark:text-green-400">
                              Correct answer: <span className="font-medium">{q.correct}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
              <p className="text-sm text-center">
                <BookOpen className="h-4 w-4 inline mr-2" />
                Continue exploring the deck to deepen your understanding. Each card has lessons to teach!
              </p>
            </div>

            <Button 
              onClick={() => {
                onComplete(score);
                onClose();
              }}
              className="w-full bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary"
            >
              Continue Your Tarot Journey
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Question {currentQuestion + 1} of {questions.length}
            </DialogTitle>
            <Badge className="bg-primary text-primary-foreground">{score} pts</Badge>
          </div>
          <DialogDescription>
            {currentQ.cardName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <p className="font-medium text-lg">{currentQ.question}</p>

            <Select
              value={answers[currentQuestion] || ''}
              onValueChange={updateAnswer}
            >
              <SelectTrigger className="w-full h-auto min-h-[50px] text-left">
                <SelectValue placeholder="Select your answer..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {currentQ.options?.map((option: string, idx: number) => (
                  <SelectItem 
                    key={idx} 
                    value={option}
                    className="cursor-pointer py-3 text-sm"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Button 
              onClick={handleSubmit} 
              className="w-full bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!answers[currentQuestion]}
            >
              {currentQuestion < questions.length - 1 ? 'Next Question' : 'Review Answers'}
            </Button>
            {!answers[currentQuestion] && (
              <p className="text-xs text-muted-foreground text-center">
                Please select an answer to continue
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}